import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ChannelSidebar from './ChannelSidebar';
import ChannelDetail from './ChannelDetail';
import ChannelSettingsPanel from './ChannelSettingsPanel';
import CreateChannelModal from 'features/channel/components/CreateChannelModal';
import { getAllChannels } from '../../../services/channelService';

export default function ChannelManagementClient() {
    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [settingsPanelOpen, setSettingsPanelOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [mobileView, setMobileView] = useState('list');

    const fetchChannels = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await getAllChannels();
            if (res?.data) {
                setChannels(res.data);
                if (res.data.length > 0 && !selectedChannel) {
                    setSelectedChannel(res.data[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching channels:", error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedChannel]);

    useEffect(() => {
        fetchChannels();
    }, [fetchChannels]);

    return (
        <div className="h-screen flex flex-col bg-[#0C0C14] overflow-hidden">

            {/* TOP NAV */}
            <header className="flex justify-between items-center px-5 py-3 bg-[#12121E] border-b border-white/10">

                <div className="flex items-center gap-4">
                    <Link to="/chat-interface" className="flex items-center gap-2">
                        <span className="text-white font-semibold">ChatFlow</span>
                    </Link>

                    <span className="hidden sm:block text-sm text-gray-400">
                        Channel Management
                    </span>
                </div>

                <div className="flex items-center gap-3">

                    <Link
                        to="/chat-interface"
                        className="hidden sm:block px-3 py-2 rounded-lg text-gray-400 hover:text-white"
                    >
                        Open Chat
                    </Link>

                    <button
                        onClick={() => setCreateModalOpen(true)}
                        className="px-4 py-2 bg-indigo-500 hover:bg-indigo-400 text-white rounded-lg text-sm font-semibold"
                    >
                        + New Channel
                    </button>

                </div>
            </header>

            {/* MAIN */}
            <div className="flex flex-1 overflow-hidden">
                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* SIDEBAR */}
                        <div className={`${mobileView === 'detail' ? 'hidden md:flex' : 'flex'}`}>
                            <ChannelSidebar
                                channels={channels}
                                selectedChannel={selectedChannel}
                                onSelect={(ch) => {
                                    setSelectedChannel(ch);
                                    setMobileView('detail');
                                }}
                                onCreateChannel={() => setCreateModalOpen(true)}
                            />
                        </div>

                        {/* DETAIL */}
                        <div className={`${mobileView === 'list' ? 'hidden md:flex' : 'flex'} flex-1`}>
                            {selectedChannel ? (
                                <ChannelDetail
                                    channel={selectedChannel}
                                    onSettingsOpen={() => setSettingsPanelOpen(true)}
                                    settingsOpen={settingsPanelOpen}
                                    onBack={() => setMobileView('list')}
                                />
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-gray-500">
                                    Select a channel to manage
                                </div>
                            )}
                        </div>

                        {/* SETTINGS PANEL */}
                        {settingsPanelOpen && selectedChannel && (
                            <div className="hidden lg:flex">
                                <ChannelSettingsPanel
                                    channel={selectedChannel}
                                    onClose={() => setSettingsPanelOpen(false)}
                                    onUpdateSuccess={fetchChannels}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* MODAL */}
            {createModalOpen && (
                <CreateChannelModal
                    onClose={() => setCreateModalOpen(false)}
                    onSuccess={() => { setCreateModalOpen(false); fetchChannels() }}
                />
            )}

        </div>
    );
}
